import { Grid, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import MenuSelectInput from '../FormInputs/MenuSelectInput';
import ToggleInput from '../FormInputs/ToggleInput';
import DiffDeptInputs from './DiffDeptInputs';
import DiffDeptInputsWithFetch from './DiffDeptInputsWithFetch';
import StaffInfoInputs from './StaffInfoInputs';
import StaffInfoModal from './StaffInfoModal';

FormStaffInfoFieldWithDiffDept.propTypes = {
  fieldName: PropTypes.string,
  focusedField: PropTypes.string,
  setFocusedField: PropTypes.func,
  fieldAttributes: PropTypes.object,
};

export default function FormStaffInfoFieldWithDiffDept({ fieldName, focusedField, setFocusedField, fieldAttributes }) {
  const staffTypes = useSelector((state) => state.transitions?.data?.staffTypes || []);

  const { control, watch, setValue, getValues } = useFormContext();
  const [isEditable, setIsEditable] = useState(false);
  const [isVacantFieldEditable, setIsVacantFieldEditable] = useState(false);
  const [isDiffDeptFieldEditable, setIsDiffDeptFieldEditable] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [diffDeptFieldName, setDiffDeptFieldName] = useState();
  const [isDataMissing, setIsDataMissing] = useState(false);

  const isVacant = watch(`${fieldName}.isVacant`);
  const isDiffDept = watch(`${fieldName}.isDiffDept`);
  const diffDeptId = watch(`${fieldName}.doneByStaffTypeId`);

  const { fields } = useFieldArray({
    control,
    name: `${fieldName}.staffMembers`,
  });

  useEffect(() => {
    if (fieldAttributes.isEditable === false) {
      setIsEditable(false);
      setIsVacantFieldEditable(false);
      setIsDiffDeptFieldEditable(false);
    } else {
      setIsEditable(!isVacant && !isDiffDept);
      setIsVacantFieldEditable(!isDiffDept);
      setIsDiffDeptFieldEditable(!isVacant);
    }

    const fieldNames = ['firstName', 'lastName', 'cellPhone', 'email'];
    const hasFieldsFilled = fields.some((_, index) =>
      fieldNames.some((x) => !!getValues(`${fieldName}.staffMembers[${index}].${x}`))
    );

    if ((isVacant || isDiffDept) && hasFieldsFilled) setOpenModal(true);
    if (!isDiffDept) setValue(`${fieldName}.doneByStaffTypeId`, null);
  }, [isVacant, isDiffDept]);

  useEffect(() => {
    const fieldName = Object.entries(getValues()).find(
      ([, value]) => value?.staffTypeId != null && String(value?.staffTypeId) === String(diffDeptId)
    )?.[0];

    if (fieldName === undefined && diffDeptId) setIsDataMissing(true);

    setDiffDeptFieldName(fieldName);
  }, [diffDeptId]);

  const onProceed = () => {
    fields.forEach((_, index) => {
      setValue(`${fieldName}.staffMembers[${index}].firstName`, null);
      setValue(`${fieldName}.staffMembers[${index}].lastName`, null);
      setValue(`${fieldName}.staffMembers[${index}].cellPhone`, null, { shouldValidate: true });
      setValue(`${fieldName}.staffMembers[${index}].email`, null, { shouldValidate: true });
    });
    setOpenModal(false);
  };

  const onClose = () => {
    setValue(`${fieldName}.isVacant`, false);
    setValue(`${fieldName}.isDiffDept`, false);
    setOpenModal(false);
  };

  const onDiffDeptIdChange = () => {
    //This needs to happen here and not in UseEffect
    setIsDataMissing(false);
  };

  return (
    <Grid>
      <Grid container>
        <Grid item>
          <Typography variant="subtitle1" color="text.secondary">
            {fieldAttributes.questionTitle}
          </Typography>
        </Grid>
        <Grid item sx={{ ml: 'auto', mr: 1 }}>
          <Controller
            name={`${fieldName}.isVacant`}
            control={control}
            render={() => (
              <ToggleInput
                fieldAttributes={{ ...fieldAttributes, label: 'Vacant', isEditable: isVacantFieldEditable }}
                fieldName={`${fieldName}.isVacant`}
              />
            )}
          />
        </Grid>
        <Grid item sx={{ mr: 1 }}>
          <Tooltip placement="top" arrow title="This role is done by someone who also works for a different department">
            <span>
              <Controller
                name={`${fieldName}.isDiffDept`}
                control={control}
                render={() => (
                  <ToggleInput
                    fieldAttributes={{ ...fieldAttributes, label: 'Diff dept', isEditable: isDiffDeptFieldEditable }}
                    fieldName={`${fieldName}.isDiffDept`}
                  />
                )}
              />
            </span>
          </Tooltip>
        </Grid>
        {isDiffDept && (
          <Grid item sx={{ alignSelf: 'flex-end' }}>
            <MenuSelectInput
              fieldName={`${fieldName}.doneByStaffTypeId`}
              fieldAttributes={{
                ...fieldAttributes,
                isEditable: isDiffDeptFieldEditable,
                options: staffTypes,
              }}
              onChangeAdditionalActions={onDiffDeptIdChange}
            />
          </Grid>
        )}
      </Grid>
      {isDiffDept && diffDeptFieldName ? (
        <DiffDeptInputs key={diffDeptFieldName} fieldName={diffDeptFieldName} />
      ) : isDiffDept && isDataMissing ? (
        <DiffDeptInputsWithFetch diffDeptId={String(diffDeptId)} />
      ) : (
        fields.map((_, index) => (
          <StaffInfoInputs
            key={index}
            fieldName={`${fieldName}.staffMembers[${index}]`}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            fieldAttributes={fieldAttributes}
            isEditable={isEditable}
          />
        ))
      )}
      <StaffInfoModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        onProceed={onProceed}
        onClose={onClose}
        fieldAttributes={fieldAttributes}
        label={isVacant ? 'vacant' : 'different department'}
      />
    </Grid>
  );
}
